import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of, ReplaySubject } from "rxjs";
import { catchError, finalize, map, take } from "rxjs/operators";
import { API_BASE_URL, Status } from "src/swagger/api.client.generated";

/** One UserOnboardingStep row as the API returns it (dates arrive as ISO strings). */
export interface OnboardingStep {
  stepKey: string;
  startedAt?: string | null;
  completedAt?: string | null;
  lastViewedAt?: string | null;
  deferredUntil?: string | null;
  dismissed: boolean;
  /** Explicit deferrals of this step (FLIP-335). */
  deferralCount: number;
}

/** Fields left undefined are left unchanged server-side. */
export interface OnboardingStepUpdate {
  stepKey: string;
  startedAt?: Date;
  completedAt?: Date;
  lastViewedAt?: Date;
  deferredUntil?: Date;
  dismissed?: boolean;
  /** Records one explicit deferral; the server increments DeferralCount. */
  incrementDeferral?: boolean;
}

/**
 * Parses a step timestamp from the API as UTC. The client always writes UTC (Date serialises via
 * toISOString), but SQL datetime2 comes back through Newtonsoft with no zone suffix, which
 * `new Date()` would otherwise read as local time and shift by the UTC offset.
 */
export function parseStepTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  return new Date(hasZone ? value : `${value}Z`);
}

interface ApiResponse<T> {
  resultStatus?: { statusCode?: number; errorText?: string };
  responseData?: T | null;
}

/**
 * Account-level onboarding lesson state (FLIP-300's UserOnboardingStep API) for the lesson
 * services that consume it - H01 (FLIP-334), H02A (FLIP-335).
 *
 * Uses the direct-HttpClient + API_BASE_URL pattern (as FeatureFlagService does) rather than the
 * NSwag-generated OnboardingService: the generated DTOs only copy the fields they were generated
 * with, so they would silently drop `deferralCount` / `incrementDeferral` (FLIP-335) and the
 * `HasSubmittedScore` endpoint (FLIP-334) - and regenerating needs a live API instance.
 *
 * Every call degrades rather than throws: a failed read reports "unknown" and a failed write is
 * swallowed, because onboarding is guidance and must never break the page it sits on.
 */
@Injectable({ providedIn: "root" })
export class OnboardingStepService {
  /**
   * The newest queued write per step key; it emits once that write has been answered. A step row has a unique index on (user, key), so
   * two writes sent together for a row that does not exist yet both try to insert it and one fails - and failed writes are swallowed,
   * so the loser (often the completion) was silently lost: UP06 came back after a refresh (FLIP-619).
   */
  private readonly writeTails = new Map<string, ReplaySubject<void>>();

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {}

  /** All of the current user's active step rows, or undefined if the read failed. */
  getSteps(): Observable<OnboardingStep[] | undefined> {
    return this.http
      .get<ApiResponse<OnboardingStep[]>>(`${this.apiBaseUrl}/api/Onboarding/GetOnboardingSteps`)
      .pipe(
        map((response) =>
          response?.resultStatus?.statusCode === Status.Success ? response.responseData ?? [] : undefined
        ),
        catchError(() => of(undefined))
      );
  }

  /**
   * The current user's step row for `stepKey`: null if none exists yet, undefined if the read
   * failed - a lesson must not treat "couldn't check" as "never seen" and replay itself.
   */
  getStep(stepKey: string): Observable<OnboardingStep | null | undefined> {
    return this.getSteps().pipe(
      map((steps) => (steps === undefined ? undefined : steps.find((s) => s.stepKey === stepKey) ?? null))
    );
  }

  /** Creates or patches the step row - fields left undefined are left unchanged server-side. */
  upsertStep(update: OnboardingStepUpdate): void {
    const key = update.stepKey;
    const finished$ = new ReplaySubject<void>(1);
    const previous$ = this.writeTails.get(key);
    this.writeTails.set(key, finished$);
    const send = () =>
      this.http
        .post(`${this.apiBaseUrl}/api/Onboarding/UpsertOnboardingStep`, update)
        .pipe(
          catchError(() => of(null)),
          finalize(() => {
            finished$.next();
            finished$.complete();
            if (this.writeTails.get(key) === finished$) {
              this.writeTails.delete(key);
            }
          })
        )
        .subscribe();
    // The first write for a key goes out at once; a later one for the same key waits for the one before it (success or failure).
    if (previous$) {
      previous$.pipe(take(1)).subscribe(send);
    } else {
      send();
    }
  }

  /**
   * Whether the user has ever submitted a Score. Resolves to null when the check fails, so callers
   * can tell "no Score" apart from "unknown" and hold back rather than guess.
   */
  hasSubmittedScore(): Observable<boolean | null> {
    return this.http
      .get<ApiResponse<boolean>>(`${this.apiBaseUrl}/api/Onboarding/HasSubmittedScore`)
      .pipe(
        map((response) =>
          response?.resultStatus?.statusCode === Status.Success ? !!response.responseData : null
        ),
        catchError(() => of(null))
      );
  }

  /**
   * How many Scores the user has submitted (FLIP-349) - B01 tells a first Score apart from
   * established Score history with it. Resolves to null when the check fails, like hasSubmittedScore().
   */
  getSubmittedScoreCount(): Observable<number | null> {
    return this.http
      .get<ApiResponse<number>>(`${this.apiBaseUrl}/api/Onboarding/GetSubmittedScoreCount`)
      .pipe(
        map((response) =>
          response?.resultStatus?.statusCode === Status.Success ? response.responseData ?? 0 : null
        ),
        catchError(() => of(null))
      );
  }

  /**
   * Whether the user has ever saved any Plan - established Plan history (spec §26.7), which
   * suppresses P02. Resolves to null when the check fails, like hasSubmittedScore().
   */
  hasSavedPlan(): Observable<boolean | null> {
    return this.getBoolean("HasSavedPlan");
  }

  /**
   * Whether the user has any saved hand - hands only, never old Plans (FLIP-619). The Play first-hand walkthrough (P02, P02A, P05, P07,
   * P11, P12) is for everyone without a hand, however much of the core journey they finished in the Plan era. null when the check fails.
   */
  hasSavedHand(): Observable<boolean | null> {
    return this.getBoolean("HasSavedHand");
  }

  /** Whether the user has a saved hand with at least one card - hands only (FLIP-619). null when the check fails. */
  hasQualifyingSavedHand(): Observable<boolean | null> {
    return this.getBoolean("HasQualifyingSavedHand");
  }

  /**
   * Whether the user has saved a qualifying hand (at least one Star Card), or an old Plan with an Activity Card and a Star Card
   * (qualifying_first_plan_saved, workbook v2.38). The core-journey predicate key, and what
   * retires the first-Plan walkthrough, H02C/H02D and B02. null when the check fails.
   */
  hasQualifyingSavedPlan(): Observable<boolean | null> {
    return this.getBoolean("HasQualifyingSavedPlan");
  }

  /**
   * How many qualifying saved hands (or old Plans) the user has - a hand with at least one Star Card, or an Activity Card and a Star Card
   * (qualifying_second_plan_saved, workbook v2.47: UP08 needs two). null when the check fails.
   */
  getQualifyingSavedPlanCount(): Observable<number | null> {
    return this.http
      .get<ApiResponse<number>>(`${this.apiBaseUrl}/api/Onboarding/GetQualifyingSavedPlanCount`)
      .pipe(
        map((response) =>
          response?.resultStatus?.statusCode === Status.Success ? response.responseData ?? 0 : null
        ),
        catchError(() => of(null))
      );
  }

  private getBoolean(action: string): Observable<boolean | null> {
    return this.http
      .get<ApiResponse<boolean>>(`${this.apiBaseUrl}/api/Onboarding/${action}`)
      .pipe(
        map((response) =>
          response?.resultStatus?.statusCode === Status.Success ? !!response.responseData : null
        ),
        catchError(() => of(null))
      );
  }
}
